<script setup lang="ts">
import { computed } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import { AppPreferencesTags } from '@libs/ui';

import type {
  ItineraryAccommodationLoaderSegment,
  ItineraryAccommodationSegment,
} from '../../../models';
import {
  isUnavailableAccommodationSegment,
  isAlternativeRoomSuggestionAccommodationSegment,
  UnavailabilityReason,
  isItineraryAccommodationLoaderSegment,
} from '../../../models';

const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

interface AccommodationSegmentHeaderProps {
  segment: ItineraryAccommodationSegment | ItineraryAccommodationLoaderSegment;
  printMode: boolean;
}

const props = defineProps<AccommodationSegmentHeaderProps>();

interface AvailableAccommodationSegmentEmits {
  (eventName: 'add-filter'): void;
  (eventName: 'remove-filter', value: { type: HotelFilter; id?: string }): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'toggle-accommodation'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
}

const emit = defineEmits<AvailableAccommodationSegmentEmits>();

const shouldShowPreferencesTags = computed<boolean>(() => {
  if (isItineraryAccommodationLoaderSegment(props.segment)) {
    return true;
  }
  if (isAlternativeRoomSuggestionAccommodationSegment(props.segment)) {
    return false;
  }
  if (isUnavailableAccommodationSegment(props.segment)) {
    return props.segment.reasons.includes(UnavailabilityReason.SelectedFilters);
  }
  return true;
});
</script>

<template>
  <div :class="['accommodation-segment-header']">
    <template v-if="!props.printMode">
      <AppPreferencesTags
        v-if="shouldShowPreferencesTags"
        :tags="props.segment.filters"
        :total-tags="props.segment.filters.length"
        :is-mobile="browserStore.isSmallerThan1200px"
        :add-preferences-text="$t('filter.add_filters')"
        :remove-icon-aria-label="$t('a11y.remove_filter')"
        :should-hide-add-button="true"
        :show-filters-aria-label="$t('a11y.show_filters')"
        class="preferences-tags"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('add-filter');
        "
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="
          emit('remove-filter', {
            type: $event.type as HotelFilter,
            id: $event.id,
          })
        "
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.accommodation-segment-header {
  width: 100%;
  display: grid;
  grid-template-columns: min-content min-content;
  grid-template-rows: auto;
  align-items: center;
  padding-left: var(--spacing-8);

  .preferences-tags {
    grid-column: 3/4;
    grid-row: 1/-1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--spacing-12);
    overflow: hidden;
  }

  @include for-less-or-equal-than-1200-px {
    display: grid;
    grid-template-columns: min-content 1fr;
    grid-template-rows: auto auto;
    align-items: start;

    .suggestion-type-selector {
      grid-column: 2/2;
      grid-row: 1/2;
      justify-content: flex-end;
    }
    .preferences-tags {
      grid-column: 3/3;
      grid-row: 1/2;
      display: flex;
      justify-content: end;
      align-items: flex-end;
    }
  }
}
</style>
